/* eslint-disable max-len */
/* eslint-disable react/prop-types */
import React, { useEffect, useState, cloneElement } from 'react'

// Hooks
import { useAppKit, useAppKitAccount, useDisconnect } from '@reown/appkit/react'
import { usePostAddressVerification } from 'core/hooks/api'

// Components
import { Button, FormNavigation, TextSliced } from 'views/components'

// Constants
import { BLOCKCHAIN_SYMBOL_MONO_SVGS } from 'common/constants/blockchainMonoSvgs'
import { WALLET_PROVIDERS } from 'common/constants/walletProviders'

// Provider
import BitcoinProvider from '../BitcoinProvider'
import EthereumProvider from '../EthereumProvider'
import SolanaProvider from '../SolanaProvider'

// Styled Elements
import {
  Wrapper,
  AddressVerificationContainer,
  AddressVerificationWrapper,
  AddressContainer,
  ButtonWrapper,
  LogoContainer,
  LogoWrapper,
  ContentContainer,
  MessageContainer,
  Text,
  Title,
} from './AddressVerificationWrapper.elements'
import PropTypes from 'prop-types'

function AddressVerification(props) {
  const { addressVerificationDetails, cipherText, setSuccessMsg, setErrMsg } = props

  // Local State
  const [page, setPage] = useState(1)
  const [message, setMessage] = useState('')
  const [signature, setSignature] = useState()
  const [pubkey, setPubkey] = useState()

  // Hooks
  const {
    postAddressVerification,
    postAddressVerificationData,
    postAddressVerificationError,
    isPostAddressVerificationLoading,
  } = usePostAddressVerification()

  const { open } = useAppKit()
  const { address } = useAppKitAccount()
  const { disconnect } = useDisconnect()
  // Only allowing bitcoin accounts for pubkeys
  const { allAccounts } = useAppKitAccount({ chainNamespace: 'bip122' })

  // Variables
  const notConnected = !address
  const connected = !!address
  const correctWallet = address && addressVerificationDetails?.address?.toLowerCase() === address?.toLowerCase()
  const incorrectWallet = address && addressVerificationDetails?.address?.toLowerCase() !== address?.toLowerCase()

  // Functions

  const handleVerification = async () => {
    if (!signature) return setSignature()
    if (signature) {
      postAddressVerification({ signature, cipher_text: cipherText, pubkey: pubkey || null })
    }
  }
  const handleDisconnect = async () => {
    await disconnect()
  }
  const generateMessage = () => {
    const msg = generateMessageSolana(
      addressVerificationDetails?.name_business,
      addressVerificationDetails?.chain,
      addressVerificationDetails?.address,
      addressVerificationDetails?.nonce,
      addressVerificationDetails?.created,
    )
    // `Ospree.io wants you to sign with your ${addressVerificationDetails?.chain} account: ${address}\n\nNonce: ${addressVerificationDetails?.nonce}\nIssued at: ${addressVerificationDetails?.created}`
    setMessage(msg)
  }
  // UseEffects
  useEffect(() => {
    if (address && page !== 2) setPage(2)
    if (!address && page !== 1) setPage(1)
    if (address) generateMessage()
    if (!address) setMessage('')
  }, [address])
  useEffect(() => {
    handleVerification()
  }, [signature])
  useEffect(() => {
    if (postAddressVerificationData && !postAddressVerificationError) setSuccessMsg('Successfully validated ownership')
  }, [postAddressVerificationData])
  useEffect(() => {
    if (postAddressVerificationError) setErrMsg('Ownership validation unsuccessful, invalid signature.')
  }, [postAddressVerificationError])
  useEffect(() => {
    allAccounts.forEach((acc) => {
      if (acc?.address?.toLowerCase() === address?.toLowerCase()) {
        setPubkey(acc.publicKey)
      }
    })
  }, [allAccounts])

  return (
    <Wrapper>
      <FormNavigation
        pageNumber={page}
        setPageNumber={setPage}
        headers={[{ label: 'Connect your wallet' }, { label: 'Verify ownership' }]}
        style={{ marginTop: '10px' }}
      />
      <AddressVerificationWrapper correctWallet={correctWallet}>
        <AddressVerificationContainer connected={connected}>
          {addressVerificationDetails?.symbol && (
            <LogoWrapper connected={connected}>
              <LogoContainer connected={connected}>
                {cloneElement(BLOCKCHAIN_SYMBOL_MONO_SVGS[addressVerificationDetails?.symbol], {
                  size: connected ? 30 : 70,
                })}
              </LogoContainer>

              <AddressContainer connected={connected} onClick={() => open({ view: 'Networks' })}>
                <TextSliced text={address} sliceLength={5} />
              </AddressContainer>
            </LogoWrapper>
          )}

          {correctWallet && (
            <MessageContainer correctWallet={correctWallet}>
              <Text style={{ textAlign: 'start', whiteSpace: 'pre-line', wordBreak: 'break-all' }}>
                {message && message}
              </Text>
            </MessageContainer>
          )}

          <ContentContainer correctWallet={correctWallet}>
            <Title color={incorrectWallet ? 'red' : ''} correctWallet={correctWallet}>
              {notConnected && 'Connect wallet'}
              {correctWallet && 'Verify ownership'}
              {incorrectWallet && 'Incorrect wallet connected'}
            </Title>

            <Text>
              {notConnected && `Connect your ${addressVerificationDetails?.chain || ''} wallet`}
              {correctWallet && 'Please sign the message below to verify ownership:'}
              {incorrectWallet && `Please connect the correct ${addressVerificationDetails?.chain || ''} wallet`}
            </Text>
          </ContentContainer>
        </AddressVerificationContainer>
      </AddressVerificationWrapper>
      <ButtonWrapper>
        {notConnected && !isPostAddressVerificationLoading && (
          <Button fullWidth type="button" onClick={() => open({ view: 'Connect' })}>
            Connect Wallet
          </Button>
        )}
        {address && !isPostAddressVerificationLoading && (
          <Button fullWidth type="button" onClick={handleDisconnect} variant="outlined">
            Disconnect Wallet
          </Button>
        )}
        {correctWallet && addressVerificationDetails?.chain === WALLET_PROVIDERS.bitcoin && (
          <BitcoinProvider
            messageToSign={message}
            setSignature={setSignature}
            address={address}
            isLoading={isPostAddressVerificationLoading}
            setErrMsg={setErrMsg}
          />
        )}
        {correctWallet && addressVerificationDetails?.chain === WALLET_PROVIDERS.ethereum && (
          <EthereumProvider
            messageToSign={message}
            setSignature={setSignature}
            address={address}
            isLoading={isPostAddressVerificationLoading}
          />
        )}
        {correctWallet && addressVerificationDetails?.chain === WALLET_PROVIDERS.solana && (
          <SolanaProvider
            messageToSign={message}
            setSignature={setSignature}
            isLoading={isPostAddressVerificationLoading}
          />
        )}
      </ButtonWrapper>
    </Wrapper>
  )
}
function generateMessageSolana(business_name, chain, address, nonce, created) {
  return (
    `${business_name || 'Ospree.io'} wants you to sign with your ${chain} account: ${address}\n` +
    '\n' +
    `Nonce: ${nonce}\n` +
    `Issued at: ${created}`
  )
}
// Default Props
AddressVerification.defaultProps = {
  addressVerificationDetails: {},
}

// Proptypes Validation
AddressVerification.propTypes = {
  addressVerificationDetails: PropTypes.shape({
    symbol: PropTypes.string,
    address: PropTypes.string,
    chain: PropTypes.string,
    created: PropTypes.string,
  }),
}

export default AddressVerification
